export class TextSoundGenerator {
    constructor(scene) {
        this.scene = scene;
        this.audioContext = null;
        this.oscillator = null;
        this.gainNode = null;
        this.filter = null;
        this.isInitialized = false;
        this.baseFrequency = 200;
        this.letterDuration = 50;
    }

    init() {
        try {
            // Create or resume AudioContext
            if (!this.audioContext) {
                this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            }
            
            // Resume AudioContext if suspended (required by modern browsers)
            if (this.audioContext.state === 'suspended') {
                this.audioContext.resume();
            }
            
            // Initialize oscillator and nodes
            this.initializeNodes();
            this.isInitialized = true;
        } catch (error) {
            console.warn('Failed to initialize TextSoundGenerator:', error);
            this.isInitialized = false;
        }
    }
    
    initializeNodes() {
        // Clean up existing nodes
        this.cleanup();
        
        this.oscillator = this.audioContext.createOscillator();
        this.oscillator.type = 'triangle';

        this.gainNode = this.audioContext.createGain();
        this.gainNode.gain.value = 0;

        this.filter = this.audioContext.createBiquadFilter();
        this.filter.type = 'bandpass';
        this.filter.frequency.value = this.baseFrequency;
        this.filter.Q.value = 1;

        this.oscillator.connect(this.filter);
        this.filter.connect(this.gainNode);
        this.gainNode.connect(this.audioContext.destination);

        this.oscillator.start();
        
        // Handle oscillator end event to reinitialize if needed
        this.oscillator.onended = () => {
            if (this.isInitialized) {
                this.initializeNodes();
            }
        };
    }

    playCharacterSound(char) {
        // Skip spaces and punctuation
        if (char === ' ' || /[.,!?]/.test(char)) {
            return;
        }
        
        // Ensure we're initialized and AudioContext is running
        if (!this.isInitialized || !this.audioContext || this.audioContext.state !== 'running') {
            this.init();
            if (!this.isInitialized) return;
        }
        
        try {
            const charCode = char.charCodeAt(0);
            const frequency = this.baseFrequency + (charCode % 10) * 20;

            this.filter.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
            this.gainNode.gain.cancelScheduledValues(this.audioContext.currentTime);
            this.gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
            this.gainNode.gain.linearRampToValueAtTime(0.15, this.audioContext.currentTime + 0.01);
            this.gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + 0.05);
        } catch (error) {
            console.warn('Error playing character sound:', error);
            // Try to reinitialize on error
            this.init();
        }
    }
    
    cleanup() {
        if (this.oscillator) {
            try {
                this.oscillator.stop();
                this.oscillator.disconnect();
            } catch (e) {
                // Oscillator might already be stopped
            }
            this.oscillator = null;
        }
        if (this.gainNode) {
            this.gainNode.disconnect();
            this.gainNode = null;
        }
        if (this.filter) {
            this.filter.disconnect();
            this.filter = null;
        }
    }

    destroy() {
        this.isInitialized = false;
        this.cleanup();
        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }
    }
}